import React, { useState, useMemo } from 'react';
import { LoadingSpinner, Button } from 'components/common';
import { FiFilter, FiX, FiChevronDown, FiChevronUp, FiSearch, FiRefreshCw } from 'react-icons/fi';
import PropTypes from 'prop-types';
const formatLabel = (str) =>
  (str || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const capitalizeSingle = (str) => {
  if (!str) return '';
  const text = (str || '').replace(/_/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

const CO_STATUS_COLORS = {
  submitted:        { dot: '#6366F1', bg: '#eef2ff', text: '#3730a3' },
  sent:             { dot: '#3B82F6', bg: '#eff6ff', text: '#1e40af' },
  accepted:         { dot: '#10B981', bg: '#ecfdf5', text: '#065f46' },
  rejected:         { dot: '#EF4444', bg: '#fef2f2', text: '#991b1b' },
  requested_revise: { dot: '#F59E0B', bg: '#fffbeb', text: '#92400e' },
  accepted_revise:  { dot: '#14b8a6', bg: '#f0fdfa', text: '#115e59' },
  rejected_and_closed: { dot: '#64748B', bg: '#f1f5f9', text: '#334155' },
};

const FilterSection = ({ title, children, count }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="tw-border tw-border-gray-100 tw-rounded-lg tw-overflow-hidden">
      <Button
        onClick={() => setOpen(o => !o)}
        variant="ghost"
      >
        <div className="tw-flex tw-items-center tw-gap-2">
          <span className="tw-text-sm tw-font-semibold tw-text-gray-700">{title}</span>
          {count > 0 && (
            <span className="tw-inline-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-rounded-xl tw-bg-blue-500 tw-text-white tw-text-xs tw-font-bold">
              {count}
            </span>
          )}
        </div>
        {open
          ? <FiChevronUp size={14} className="tw-text-gray-400" />
          : <FiChevronDown size={14} className="tw-text-gray-400" />
        }
      </Button>
      {open && (
        <div className="tw-px-4 tw-py-3 tw-space-y-1.5 tw-max-h-56 tw-overflow-y-auto tw-bg-white">
          {children}
        </div>
      )}
    </div>
  );
};

FilterSection.propTypes = {
  title:    PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  count:    PropTypes.number
};
FilterSection.defaultProps = { count: 0 };

const CheckItem = ({ label, checked, onChange, color }) => (
  <label className="tw-flex tw-items-center tw-gap-2.5 tw-py-1.5 tw-px-2 tw-rounded hover:tw-bg-gray-50 tw-cursor-pointer tw-transition-colors">
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="tw-w-4 tw-h-4 tw-rounded-sm tw-border-gray-300 tw-text-blue-600 tw-cursor-pointer focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-ring-offset-1"
    />
    {color && (
      <span
        className="tw-w-2 tw-h-2 tw-rounded-xl tw-flex-shrink-0"
        style={{ backgroundColor: color.dot }}
      />
    )}
    <span
      className="tw-text-sm tw-select-none tw-flex-1 tw-leading-snug"
      style={color ? { color: color.text } : { color: '#374151' }}
    >
      {label}
    </span>
  </label>
);

CheckItem.propTypes = {
  label:    PropTypes.string.isRequired,
  checked:  PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  color:    PropTypes.shape({ dot: PropTypes.string, text: PropTypes.string })
};
CheckItem.defaultProps = { checked: false, color: null };

const SearchInput = ({ value, onChange, placeholder }) => (
  <div className="tw-relative tw-mb-2">
    <FiSearch className="tw-absolute tw-left-2.5 tw-top-2.5 tw-text-gray-400" size={14} />
    <input
      type="text"
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="tw-w-full tw-pl-8 tw-pr-3 tw-py-2 tw-text-sm tw-border tw-border-gray-200 tw-rounded focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500"
    />
  </div>
);

SearchInput.propTypes = {
  value:       PropTypes.string.isRequired,
  onChange:    PropTypes.func.isRequired,
  placeholder: PropTypes.string
};
SearchInput.defaultProps = { placeholder: 'Search...' };

// Text input filter for free-text search fields
const TextFilterSection = ({ title, value, onChange, placeholder, active }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="tw-border tw-border-gray-100 tw-rounded-lg tw-overflow-hidden">
      <Button
        variant="ghost"
        onClick={() => setOpen(o => !o)}
      >
        <div className="tw-flex tw-items-center tw-gap-2">
          <span className="tw-text-sm tw-font-semibold tw-text-gray-700">{title}</span>
          {active && (
            <span className="tw-inline-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-rounded-xl tw-bg-blue-500 tw-text-white tw-text-xs tw-font-bold">
              1
            </span>
          )}
        </div>
        {open
          ? <FiChevronUp size={14} className="tw-text-gray-400" />
          : <FiChevronDown size={14} className="tw-text-gray-400" />
        }
      </Button>
      {open && (
        <div className="tw-px-4 tw-py-3 tw-bg-white">
          <div className="tw-relative">
            <FiSearch className="tw-absolute tw-left-2.5 tw-top-2.5 tw-text-gray-400" size={14} />
            <input
              type="text"
              placeholder={placeholder}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              className="tw-w-full tw-pl-8 tw-pr-3 tw-py-2 tw-text-sm tw-border tw-border-gray-200 tw-rounded focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500"
            />
          </div>
        </div>
      )}
    </div>
  );
};

TextFilterSection.propTypes = {
  title:       PropTypes.string.isRequired,
  value:       PropTypes.string.isRequired,
  onChange:    PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  active:      PropTypes.bool
};
TextFilterSection.defaultProps = { placeholder: 'Search...', active: false };

// Groups units by the `region` column.
// If region is blank, falls back to the unit's own name as a standalone group.
const buildGroups = (units, search) => {
  const q = search.toLowerCase();
  const groups = {};

  units.forEach(unit => {
    const groupKey = unit.region || unit.name;
    const label    = [unit.name, unit.description].filter(Boolean).join(' - ');
    if (!groups[groupKey]) groups[groupKey] = [];
    if (!q || label.toLowerCase().includes(q) || groupKey.toLowerCase().includes(q)) {
      groups[groupKey].push(unit);
    }
  });

  return Object.entries(groups)
    .filter(([, items]) => items.length > 0)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([region, items]) => ({
      region,
      // Sort: units whose name == region (the parent itself) come first, then alphabetical
      units: items.sort((a, b) => {
        if (a.name === region) return -1;
        if (b.name === region) return 1;
        return a.name.localeCompare(b.name);
      }),
    }));
};

const SalesUnitFilter = ({ units, selected, search, onSearch, onToggle, onToggleGroup, count }) => {
  const groups = useMemo(() => buildGroups(units, search), [units, search]);

  return (
    <FilterSection title="Sales Unit" count={count}>
      {units.length > 0 ? (
        <>
          <SearchInput value={search} onChange={onSearch} placeholder="Search sales unit..." />
          {groups.map(({ region, units: groupUnits }) => {
            const ids      = groupUnits.map(u => u.id);
            const allOn    = ids.every(id => selected.includes(id));
            const someOn   = !allOn && ids.some(id => selected.includes(id));

            return (
              <div key={region} className="tw-mb-1">
                {/* Region header row */}
                <label className="tw-flex tw-items-center tw-gap-2 tw-px-2 tw-py-1 tw-rounded-md tw-bg-gray-50 tw-cursor-pointer hover:tw-bg-blue-50 tw-transition-colors">
                  <input
                    type="checkbox"
                    checked={allOn}
                    ref={el => { if (el) el.indeterminate = someOn; }}
                    onChange={() => onToggleGroup(ids)}
                    className="tw-w-4 tw-h-4 tw-rounded-sm tw-border-gray-300 tw-text-blue-600 tw-cursor-pointer focus:tw-ring-2 focus:tw-ring-blue-500"
                  />
                  <span className="tw-text-xs tw-font-bold tw-text-blue-700 tw-uppercase tw-tracking-wide tw-select-none tw-flex-1">
                    {region}
                  </span>
                  <span className="tw-text-xs tw-text-gray-400">{ids.length}</span>
                </label>

                {/* Child units */}
                <div className="tw-pl-4 tw-mt-0.5 tw-space-y-0.5">
                  {groupUnits.map(unit => {
                    const label = [unit.name, unit.description].filter(Boolean).join(' - ');
                    return (
                      <CheckItem
                        key={unit.id}
                        label={label}
                        checked={selected.includes(unit.id)}
                        onChange={() => onToggle(unit.id)}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
        </>
      ) : (
        <p className="tw-text-xs tw-text-gray-400 tw-italic tw-py-1">No sales units available</p>
      )}
    </FilterSection>
  );
};

SalesUnitFilter.propTypes = {
  units:         PropTypes.array.isRequired,
  selected:      PropTypes.array.isRequired,
  search:        PropTypes.string.isRequired,
  onSearch:      PropTypes.func.isRequired,
  onToggle:      PropTypes.func.isRequired,
  onToggleGroup: PropTypes.func.isRequired,
  count:         PropTypes.number,
};
SalesUnitFilter.defaultProps = { count: 0 };

const FilterSidebar = ({
  filterOptions,
  currFilters,
  onApplyFilters,
  onResetFilters,
  loading
}) => {
  const [localFilters, setLocalFilters] = useState(currFilters || {
    building_statuses: [],
    co_statuses:       [],
    sales_units:       [],
    cse_heads:         [],
    job_number:        ''
  });
  const [isOpen, setIsOpen] = useState(false);

  // Search state for list filters
  const [buildingStatusSearch, setBuildingStatusSearch] = useState('');
  const [coStatusSearch,       setCoStatusSearch]       = useState('');
  const [salesUnitSearch,      setSalesUnitSearch]      = useState('');
  const [cseHeadSearch,        setCseHeadSearch]        = useState('');


  const toggle = (filterKey, value) => {
    setLocalFilters(prev => {
      const current = prev[filterKey] || [];
      const next    = current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value];
      return { ...prev, [filterKey]: next };
    });
  };

  const handleApply = () => {
    onApplyFilters?.(localFilters);
    setIsOpen(false);
  };

  const handleReset = () => {
    const empty = {
      building_statuses: [],
      co_statuses:       [],
      sales_units:       [],
      cse_heads:         [],
      job_number:        ''
    };
    setLocalFilters(empty);
    onResetFilters?.();
  };

  const activeCount =
    (localFilters.building_statuses?.length || 0) +
    (localFilters.co_statuses?.length       || 0) +
    (localFilters.sales_units?.length       || 0) +
    (localFilters.cse_heads?.length         || 0) +
    (localFilters.job_number?.trim()        ? 1 : 0);

  const buildingStatusOptions = (filterOptions?.building_statuses || []).map(s => ({ id: s.id, name: s.name }));

  if (loading) {
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-py-8">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  // Panel content — shared between desktop and mobile slide-over
  const panelContent = (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-4 tw-border-b tw-border-gray-100 tw-flex-shrink-0">
        <div className="tw-flex tw-items-center tw-gap-2">
          <div className="tw-w-7 tw-h-7 tw-rounded tw-bg-blue-50 tw-flex tw-items-center tw-justify-center">
            <FiFilter size={14} className="tw-text-blue-600" />
          </div>
          <span className="tw-font-bold tw-text-gray-800 tw-text-sm">Filters</span>
          {activeCount > 0 && (
            <span className="tw-inline-flex tw-items-center tw-justify-center tw-px-2 tw-py-0.5 tw-rounded-xl tw-bg-blue-500 tw-text-white tw-text-xs tw-font-bold tw-min-w-[20px]">
              {activeCount}
            </span>
          )}
        </div>
        <Button
          className="lg:tw-hidden tw-p-1 tw-rounded tw-text-gray-400 hover:tw-text-gray-600 hover:tw-bg-gray-100 tw-transition-colors"
          onClick={() => setIsOpen(false)}
          aria-label="Close filters"
        >
          <FiX size={18} />
        </Button>
      </div>

      {/* Filter sections — scrollable */}
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }} className="tw-px-4 tw-py-4 tw-space-y-3">
        {/* Building Status */}
        <FilterSection
          title="Building Status"
          count={localFilters.building_statuses?.length || 0}
        >
          {buildingStatusOptions.length > 0 ? (
            <>
              <SearchInput
                value={buildingStatusSearch}
                onChange={setBuildingStatusSearch}
                placeholder="Search building status..."
              />
              {buildingStatusOptions
                .filter(s => capitalizeSingle(s.name).toLowerCase().includes(buildingStatusSearch.toLowerCase()))
                .map(status => (
                  <CheckItem
                    key={status.id}
                    label={capitalizeSingle(status.name)}
                    checked={localFilters.building_statuses?.includes(status.name) || false}
                    onChange={() => toggle('building_statuses', status.name)}
                  />
                ))}
            </>
          ) : (
            <p className="tw-text-xs tw-text-gray-400 tw-italic tw-py-1">No statuses available</p>
          )}
        </FilterSection>

        {/* CO# Status */}
        <FilterSection
          title="CO# Status"
          count={localFilters.co_statuses?.length || 0}
        >
          {(filterOptions?.co_statuses || []).length > 0 ? (
            <>
              <SearchInput
                value={coStatusSearch}
                onChange={setCoStatusSearch}
                placeholder="Search CO status..."
              />
              {(filterOptions.co_statuses || [])
                .filter(status => formatLabel(status).toLowerCase().includes(coStatusSearch.toLowerCase()))
                .map(status => (
                  <CheckItem
                    key={status}
                    label={formatLabel(status)}
                    checked={localFilters.co_statuses?.includes(status) || false}
                    onChange={() => toggle('co_statuses', status)}
                    color={CO_STATUS_COLORS[status]}
                  />
                ))}
            </>
          ) : (
            <p className="tw-text-xs tw-text-gray-400 tw-italic tw-py-1">No statuses available</p>
          )}
        </FilterSection>

        {/* Sales Units — grouped by parent/region */}
        <SalesUnitFilter
          units={filterOptions?.sales_units || []}
          selected={localFilters.sales_units || []}
          search={salesUnitSearch}
          onSearch={setSalesUnitSearch}
          onToggle={(id) => toggle('sales_units', id)}
          onToggleGroup={(ids) => {
            setLocalFilters(prev => {
              const current = prev.sales_units || [];
              const allOn   = ids.every(id => current.includes(id));
              return {
                ...prev,
                sales_units: allOn
                  ? current.filter(id => !ids.includes(id))
                  : [...new Set([...current, ...ids])]
              };
            });
          }}
          count={localFilters.sales_units?.length || 0}
        />

        {/* CSE In-Charge */}
        <FilterSection
          title="CSE In-Charge"
          count={localFilters.cse_heads?.length || 0}
        >
          {(filterOptions?.cse_heads || []).length > 0 ? (
            <>
              <SearchInput
                value={cseHeadSearch}
                onChange={setCseHeadSearch}
                placeholder="Search CSE name..."
              />
              {(filterOptions.cse_heads || [])
                .filter(cse => (cse || '').toLowerCase().includes(cseHeadSearch.toLowerCase()))
                .map(cse => (
                  <CheckItem
                    key={cse}
                    label={cse}
                    checked={localFilters.cse_heads?.includes(cse) || false}
                    onChange={() => toggle('cse_heads', cse)}
                  />
                ))}
            </>
          ) : (
            <p className="tw-text-xs tw-text-gray-400 tw-italic tw-py-1">No CSE data available</p>
          )}
        </FilterSection>

        {/* Job Number */}
        <TextFilterSection
          title="Job Number"
          value={localFilters.job_number || ''}
          onChange={(val) => setLocalFilters(prev => ({ ...prev, job_number: val }))}
          placeholder="e.g. J12345 or P6789"
          active={!!(localFilters.job_number?.trim())}
        />
      </div>

      {/* Footer — pinned at bottom, never scrolls away */}
      <div
        className="tw-px-4 tw-py-4 tw-border-t tw-border-gray-100 tw-space-y-2 tw-bg-white tw-flex-shrink-0"
        style={{ boxShadow: '0 -4px 6px -1px rgba(0,0,0,0.05)' }}
      >
        <Button
          type="button"
          onClick={handleApply}
          variant="primary"
          className="tw-w-full"
        >
          Apply Filters
        </Button>
        <Button
          type="button"
          onClick={handleReset}
          disabled={activeCount === 0}
          variant="ghost"
          className="tw-w-full"
        >
          <FiRefreshCw size={13} />
          Reset all
        </Button>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile toggle button */}
      <div className="lg:tw-hidden tw-mb-4">
        <Button
          onClick={() => setIsOpen(!isOpen)}
          variant="ghost"
        >
          <FiFilter size={15} className="tw-text-blue-500" />
          Filters
          {activeCount > 0 && (
            <span className="tw-inline-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-rounded-xl tw-bg-blue-500 tw-text-white tw-text-xs tw-font-bold">
              {activeCount}
            </span>
          )}
        </Button>
      </div>

      {/* Desktop panel — fixed height so footer is always visible */}
      <div
        className="tw-hidden lg:tw-block tw-w-full tw-bg-white tw-border tw-border-gray-200 tw-rounded-2xl tw-shadow-sm tw-overflow-hidden"
        style={{ height: 'max(320px, calc(100dvh - 110px))' }}
      >
        {panelContent}
      </div>

      {/* Mobile slide-over */}
      {isOpen && (
        <>
          <div
            className="tw-fixed tw-inset-0 tw-bg-black/40 tw-z-40 lg:tw-hidden"
            onClick={() => setIsOpen(false)}
          />
          <div className="tw-fixed tw-top-0 tw-left-0 tw-h-full tw-w-72 tw-bg-white tw-z-50 tw-shadow-2xl lg:tw-hidden tw-overflow-hidden">
            {panelContent}
          </div>
        </>
      )}
    </>
  );
};

FilterSidebar.propTypes = {
  filterOptions: PropTypes.shape({
    building_statuses: PropTypes.arrayOf(PropTypes.shape({
      id:   PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string
    })),
    co_statuses: PropTypes.arrayOf(PropTypes.string),

    cse_heads:   PropTypes.arrayOf(PropTypes.string),
    sales_units: PropTypes.arrayOf(PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        id:          PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name:        PropTypes.string,
        description: PropTypes.string,
        region:      PropTypes.string
      })
    ]))
  }),
  currFilters: PropTypes.shape({
    building_statuses: PropTypes.arrayOf(PropTypes.string),
    co_statuses:       PropTypes.arrayOf(PropTypes.string),
    sales_units:       PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    cse_heads:         PropTypes.arrayOf(PropTypes.string),
    job_number:        PropTypes.string
  }),
  onApplyFilters: PropTypes.func,
  onResetFilters: PropTypes.func,
  loading:        PropTypes.bool
};

FilterSidebar.defaultProps = {
  filterOptions:  { co_statuses: [], cse_heads: [], sales_units: [] },
  currFilters:    { building_statuses: [], co_statuses: [], sales_units: [], cse_heads: [], job_number: '' },
  onApplyFilters: null,
  onResetFilters: null,
  loading:        false
};

export default FilterSidebar;
